import { useState, useCallback } from "react";

// Active loader statuses - text_after_tool is NOT included because it hides the loader (like response_started)
export type LoaderStatus = 'submitting' | 'message_received' | 'calling_llm' | 'tool_executing' | 'processing_tool_result' | null;

export interface FirstTokenDetectionResult {
  /** Whether loader should be shown (false = show, true = hide, null = initial) */
  firstTokenReceived: boolean | null;
  /** Current loader animation status */
  currentStatus: LoaderStatus;
  /** Reset loader state */
  resetFirstTokenReceived: () => void;
  /** Wrapper for submit - sets 'submitting' status */
  wrappedSubmit: (submitFn: (input: any, config?: any) => Promise<any>) => (input: any, config?: any) => Promise<any>;
  /** Direct status change handler - called when status_change events arrive from backend */
  handleStatusChange: (status: string) => void;
}

/**
 * Manage loader visibility state based on streaming status events.
 *
 * Status flow:
 * 1. submitting - User submitted message (set by wrappedSubmit)
 * 2. message_received - Backend received message (via handleStatusChange)
 * 3. calling_llm - Backend calling LLM (via handleStatusChange)
 * 4. response_started - LLM started responding (via handleStatusChange) → hide loader
 *
 * @returns Object with loader state and handlers
 */
export function useFirstTokenDetection(): FirstTokenDetectionResult {
  const [firstTokenReceived, setFirstTokenReceived] = useState<boolean | null>(null);
  const [currentStatus, setCurrentStatus] = useState<LoaderStatus>(null);

  const resetFirstTokenReceived = useCallback(() => {
    setFirstTokenReceived(null);
    setCurrentStatus(null);
  }, []);

  // Wrapper for submit - sets initial 'submitting' status
  const wrappedSubmit = useCallback(
    (submitFn: (input: any, config?: any) => Promise<any>) => {
      return async (input: any, config?: any) => {
        setFirstTokenReceived(false);
        setCurrentStatus('submitting');
        return await submitFn(input, config);
      };
    },
    [],
  );

  // Direct status change handler - called via onStatusChange callback from streaming
  const handleStatusChange = useCallback((status: string) => {
    if (status === "message_received") {
      setCurrentStatus('message_received');
      setFirstTokenReceived(false);
    } else if (status === "calling_llm") {
      setCurrentStatus('calling_llm');
    } else if (status === "response_started") {
      setFirstTokenReceived(true);
      setCurrentStatus(null);
    } else if (status === "tool_executing") {
      // Tool is executing - ToolInput shows its own spinner, but we track status
      setFirstTokenReceived(false);
      setCurrentStatus('tool_executing');
    } else if (status === "processing_tool_result") {
      // After tool execution, show loader while waiting for next LLM response
      setFirstTokenReceived(false);
      setCurrentStatus('processing_tool_result');
    } else if (status === "text_after_tool") {
      // Text generation resuming after tool cycle - HIDE loader (same as response_started)
      // Text is now streaming via TypewriterText, no need for ChatLoader
      setCurrentStatus(null);
      setFirstTokenReceived(true);
    }
    // Unknown statuses are silently ignored
  }, []);

  return { firstTokenReceived, currentStatus, resetFirstTokenReceived, wrappedSubmit, handleStatusChange };
}
